import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import useUserStore from '~/store/modules/user'
import ImageServer, { putEnterpriseImage } from '~/services/EnterpriseImageServer'
import { canAccessEnterpriseImage, canEditEnterpriseImage, enterpriseImageUrl, imageMime } from '~/utils/enterpriseImage'
import { decodeImageBlob, encodeImageBlob, imageToPdf, saveImageDownload } from '~/utils/enterpriseImageExport'

/** 工作台与历史查看共用：只相信鉴权后的记录及 compare，不接受 URL 传来的任意文件名。 */
export function useEnterpriseImageResult(id: Ref<number>) {
  const user = useUserStore()
  const runtime = useRuntimeConfig()
  const record = ref<any>(null)
  const comparison = ref<any>(null)
  const loading = ref(false)
  const busy = ref(false)
  const errorKey = ref('')
  const editorMode = ref<'overlay' | 'region'>('overlay')
  const editorVisible = ref(false)
  const version = ref(Date.now())
  let epoch = 0
  let controller = new AbortController()
  const identity = computed(() => user.isUserInfoReady && user.token && user.userInfo.id && user.userInfo.bizTeamId
    ? `${runtime.public.apiUrl}|${user.token}|${user.userInfo.id}|${user.userInfo.bizTeamId}` : '')
  const canView = computed(() => !!identity.value && canAccessEnterpriseImage(record.value, user.userInfo, 'view'))
  const canDownload = computed(() => canView.value && canAccessEnterpriseImage(record.value, user.userInfo, 'download'))
  const canEdit = computed(() => canView.value && canEditEnterpriseImage(record.value, user.userInfo))
  const sourceUrl = computed(() => canView.value ? enterpriseImageUrl(String(runtime.public.downloadUrl), comparison.value?.sourceBlobName || '', true, version.value) : '')
  const resultUrl = computed(() => canView.value ? enterpriseImageUrl(String(runtime.public.downloadUrl), comparison.value?.operateBlobName || '', true, version.value) : '')
  const filename = computed(() => comparison.value?.sourceFileName || record.value?.origFileName || 'image')
  const current = (generation: number) => generation === epoch && !controller.signal.aborted && !!identity.value

  function clear() {
    ++epoch; controller.abort(); controller = new AbortController()
    record.value = comparison.value = null; errorKey.value = ''; loading.value = busy.value = editorVisible.value = false
  }
  function suspend() {
    ++epoch; controller.abort(); controller = new AbortController()
    busy.value = loading.value = editorVisible.value = false
  }
  async function refresh(generation: number) {
    const [detail, compare] = await Promise.all([ImageServer.detail(id.value, controller.signal), ImageServer.compare(id.value, controller.signal)])
    if (!current(generation)) return false
    if (String(detail?.code) !== '1' || String(compare?.code) !== '1'
      || Number(detail.data?.id) !== id.value || Number(compare.data?.operateRecordId) !== id.value
      || !canAccessEnterpriseImage(detail.data, user.userInfo, 'view')
      || compare.data?.operateBlobName !== detail.data?.blobFileName
      || !enterpriseImageUrl(String(runtime.public.downloadUrl), compare.data?.sourceBlobName || '')
      || !enterpriseImageUrl(String(runtime.public.downloadUrl), compare.data?.operateBlobName || '')) {
      record.value = comparison.value = null; editorVisible.value = false
      throw new Error('bizImage_err_permission')
    }
    record.value = detail.data; comparison.value = compare.data
    return true
  }
  async function load() {
    clear()
    if (!identity.value || !Number.isSafeInteger(id.value) || id.value <= 0) { errorKey.value = 'bizImage_err_permission'; return }
    const generation = epoch; loading.value = true
    try { await refresh(generation) }
    catch { if (current(generation)) errorKey.value = 'bizImage_err_permission' }
    finally { if (current(generation)) loading.value = false }
  }
  async function openEditor(mode: 'overlay' | 'region') {
    if (busy.value || loading.value || !canEdit.value) return
    const generation = epoch; busy.value = true; errorKey.value = ''
    try {
      if (await refresh(generation) && canEdit.value) { editorMode.value = mode; editorVisible.value = true }
      else if (current(generation)) errorKey.value = 'bizImage_err_permission'
    } catch { if (current(generation)) errorKey.value = 'bizImage_err_permission' }
    finally { if (current(generation)) busy.value = false }
  }
  async function save(pngBlob: Blob) {
    if (busy.value || !editorVisible.value || !canEdit.value) return
    const generation = epoch; busy.value = true; errorKey.value = ''
    try {
      if (!await refresh(generation)) return
      if (!canEdit.value) throw new Error('bizImage_err_permission')
      const signed = await ImageServer.overwrite(id.value, controller.signal)
      if (!current(generation)) return
      const mime = imageMime(record.value.blobFileName)
      if (String(signed?.code) !== '1' || signed.data?.blobFileName !== record.value.blobFileName || !mime
        || signed.data?.contentType !== mime || !signed.data?.awsUploadUrl) throw new Error('bizImage_err_permission')
      const output = await encodeImageBlob(pngBlob, mime, controller.signal)
      if (!current(generation)) return
      await putEnterpriseImage(signed.data.awsUploadUrl, output, mime, controller.signal)
      if (current(generation)) { version.value = Math.max(Date.now(), version.value + 1); editorVisible.value = false }
    } catch (error) {
      if (current(generation)) errorKey.value = error instanceof Error && error.message === 'bizImage_err_permission' ? error.message : 'bizImage_err_upload'
    } finally { if (current(generation)) busy.value = false }
  }
  async function download(type: 'image' | 'pdf') {
    if (busy.value || loading.value || !canDownload.value) return
    const generation = epoch; busy.value = true; errorKey.value = ''
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      if (!await refresh(generation)) return
      if (!canDownload.value) throw new Error('bizImage_err_permission')
      const request = new AbortController()
      const abort = () => request.abort()
      controller.signal.addEventListener('abort', abort, { once: true })
      timeout = setTimeout(abort, 30000)
      let blob: Blob
      try {
        const url = enterpriseImageUrl(String(runtime.public.downloadUrl), comparison.value.operateBlobName, false, Date.now(), filename.value)
        const response = await fetch(url, { signal: request.signal, credentials: 'omit', cache: 'no-store' })
        if (!response.ok) throw new Error('bizImage_err_export')
        blob = await response.blob()
      } finally { clearTimeout(timeout); controller.signal.removeEventListener('abort', abort) }
      if (!current(generation)) return
      const mime = imageMime(comparison.value.operateBlobName)
      if (!mime) throw new Error('bizImage_err_format')
      await decodeImageBlob(blob, controller.signal)
      // 图片下载保留最终对象原始字节，避免 JPEG/WebP 再编码造成二次有损压缩。
      const finalImage = blob.type === mime ? blob : new Blob([blob], { type: mime })
      const output = type === 'pdf' ? await imageToPdf(finalImage, controller.signal) : finalImage
      if (!current(generation)) return
      const extension = type === 'pdf' ? 'pdf' : comparison.value.operateBlobName.split('.').pop().toLowerCase()
      saveImageDownload(output, `${filename.value.replace(/\.[^.]+$/, '')}.${extension}`)
    } catch (error) {
      if (current(generation)) errorKey.value = error instanceof Error && error.message === 'bizImage_err_permission' ? error.message : 'bizImage_err_export'
    } finally { clearTimeout(timeout); if (current(generation)) busy.value = false }
  }
  watch(() => [id.value, identity.value], () => { void load() }, { immediate: true, flush: 'sync' })
  onScopeDispose(clear)
  return { record, sourceUrl, resultUrl, filename, loading, busy, errorKey, canView, canEdit, canDownload, editorVisible, editorMode, load, openEditor, save, download, suspend }
}
